'use client';

import { ChangeEvent, FormEvent, useMemo } from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import MagnifyingGlass from '@/icons/react/MagnifyingGlass';
import X from '@/icons/react/X';
import { useSiteSearch } from '@/providers';

export function SiteSearchInput() {
  const router = useRouter();
  const { query, setQuery, search, setIsOpen, inputRef } = useSiteSearch();

  // debounced user input, wait for 1 second to pass
  // another option here is waiting for meaningful whitespace
  // the debounce function needs to be memoized because these functions
  // are being redefined over and over again here.
  //
  // search, the function, is also redefined over and over again, so
  // we DO NOT want to add that to the deps list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useMemo(() => debounce(search, 1000), []);

  function handleSubmit(
    event: FormEvent<HTMLFormElement>,
    queryString: string,
  ) {
    event.preventDefault();

    if (queryString) {
      router.push(`/search?query=${queryString}`);
    }

    trackEvent(EVENT_NAMES.site_nav_search_form_submit, {
      query: queryString,
    });

    setQuery('');
    setIsOpen(false);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const newQuery = event.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery);
  }

  return (
    <form
      onSubmit={(event) => handleSubmit(event, query)}
      className="flex items-center gap-2"
    >
      <div className="grow flex items-center gap-3 text-body-450 pl-5 pr-3 py-3 focus-within:outline-none focus-within:ring border border-primary rounded-2">
        <MagnifyingGlass className="shrink-0" />
        <input
          type="search"
          className={classNames('search-cancel:hidden focus:outline-none grow')}
          onChange={onChange}
          value={query}
          placeholder="Search 1440..."
          id="site-search"
          ref={inputRef}
        />
        <button
          type="button"
          onClick={() => setQuery('')}
          className={classNames(
            'shrink-0 flex items-center justify-center w-8 h-8 rounded-1 hover:bg-gray-200 transition-all',
            query ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          <X className="" />
        </button>
      </div>
    </form>
  );
}
